import {
    FormControl,
    makeStyles,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
} from '@material-ui/core';
import * as React from 'react';
import type { KitType } from '../actions/getKitTypes';
import useKits, { Kit, LabProcessStatus } from '../hooks/useKits';
import { ReactComponent as ProgressIcon } from '../icons/progress.svg';
import useStore from '../store/store';
import Modal from './Modal';

interface KitProgressProps {
    kit?: Kit;
    memberId?: number;
    open: boolean;
    onClose: () => void;
    isAdmin?: boolean;
}

const KitProgress = ({
    kit,
    memberId,
    open,
    onClose,
    isAdmin = false,
}: KitProgressProps) => {
    const classes = useStyles();
    const kitTypes = useStore((state) => state.kitTypes);
    const kits = useKits({ memberId: memberId || 0, isAdmin: isAdmin });
    const [kitOption, setKitOption] = React.useState<number | ''>('');
    const selectedKit = kit || kits.find((k) => k.KitId === kitOption);

    React.useEffect(() => {
        if (kits?.length > 0 && kitOption === '') {
            setKitOption(kits[0]?.KitId || '');
        }
    }, [kits, kitOption]);

    const handleClose = () => {
        setKitOption('');
        onClose();
    };

    const stage1Steps = [
        LabProcessStatus.Ordered,
        LabProcessStatus.Registration,
    ];

    const stage2Steps = [
        LabProcessStatus.Extraction,
        LabProcessStatus.QCExtraction,
        LabProcessStatus.Amplification,
        LabProcessStatus.QCAmplification,
        LabProcessStatus.Sequencing,
    ];

    const stage3Steps = [
        LabProcessStatus.DataAnalysis,
        LabProcessStatus.QCData,
        LabProcessStatus.Reporting,
        LabProcessStatus.Released,
    ];

    return (
        <Modal
            open={open}
            title="Test Progress"
            onClose={handleClose}
            icon={<ProgressIcon />}
        >
            {kits && kits.length > 0 ? (
                <div className={classes.selectTest}>
                    <div className={classes.chooseTestText}>Choose test:</div>
                    <FormControl fullWidth margin="dense">
                        <Select
                            id="kit-select"
                            name="kit"
                            onChange={(e) =>
                                setKitOption(Number(e.target.value))
                            }
                            required
                            displayEmpty
                            value={kitOption}
                        >
                            {kits.map((kit: Kit) => (
                                <MenuItem key={kit.KitId} value={kit.KitId}>
                                    {kit.Nickname ||
                                        kitTypes.find(
                                            (kitType: KitType) =>
                                                kitType.KitTypeId ===
                                                kit.KitTypeId
                                        )?.Type}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            ) : (
                !selectedKit && <div>No tests found</div>
            )}
            {selectedKit && (
                <div className={classes.stages}>
                    <div className={classes.stage}>
                        <div className={classes.stageTitle}>
                            STAGE 1 - Pre-Analytical
                        </div>
                        <Stepper
                            activeStep={
                                stage1Steps.includes(
                                    selectedKit.LabProcessStatus
                                )
                                    ? stage1Steps.indexOf(
                                          selectedKit.LabProcessStatus
                                      )
                                    : selectedKit.LabProcessStatus ===
                                        LabProcessStatus.Rejected
                                      ? -1
                                      : stage1Steps.length
                            }
                            alternativeLabel
                            className={classes.stepper}
                        >
                            {stage1Steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <div className={classes.stageTitle}>
                            STAGE 2 - Analytical
                        </div>
                        <Stepper
                            activeStep={
                                stage2Steps.includes(
                                    selectedKit.LabProcessStatus
                                )
                                    ? stage2Steps.indexOf(
                                          selectedKit.LabProcessStatus
                                      )
                                    : stage3Steps.includes(
                                            selectedKit.LabProcessStatus
                                        )
                                      ? stage2Steps.length
                                      : -1
                            }
                            alternativeLabel
                            className={classes.stepper}
                        >
                            {stage2Steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        <div className={classes.stageTitle}>
                            STAGE 3 - Post-Analytical
                        </div>
                        <Stepper
                            activeStep={
                                selectedKit.LabProcessStatus ===
                                LabProcessStatus.Released
                                    ? stage3Steps.length
                                    : stage3Steps.indexOf(
                                          selectedKit.LabProcessStatus
                                      )
                            }
                            alternativeLabel
                            className={classes.stepper}
                        >
                            {stage3Steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>
                </div>
            )}
        </Modal>
    );
};

const useStyles = makeStyles((theme) => ({
    selectTest: {
        display: 'flex',
        alignItems: 'center',
        gap: '36px',
        whiteSpace: 'nowrap',
    },
    chooseTestText: {
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: '-0.36px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    stages: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '24px',
        alignSelf: 'stretch',
    },
    stage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '24px',
        alignSelf: 'stretch',
    },
    stageTitle: {
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: '-0.36px',
    },
    stepper: {
        '& .MuiStepIcon-active': { color: theme.palette.primary.main },
        '& .MuiStepIcon-completed': { color: '#A9EABA' },
        backgroundColor: theme.palette.primary.lighter,
        width: '100%',
        borderRadius: '12px',
        minWidth: '852px',
    },
}));

export default KitProgress;
